import React, {CSSProperties, FunctionComponent, useContext, useEffect, useState} from 'react';
import {Link, useLocation} from "react-router-dom";
import './TabsNavigation.scss';
import {FileSection} from "../../../models/file.model";
import {FileContext} from "../../../context/FileContext";
import {Invoice} from "../../../models/invoice.model";
import {InvoiceContext} from "../../../context/InvoiceContext";
import {ThemeContext} from "../../../context/ThemeContext";
import {Theme} from "../../../models/theme.model";
import {ActivityContext} from "../../../context/ActivityContext";
import {CompanyContext} from "../../../context/CompanyContext";
import {isOfflineMode} from "../../../utils/util-functions";

interface TabsNavigationProps {

}

interface TabHighlighterAttributes {
    left: number,
    width: number
}

export const TabsNavigation: FunctionComponent<TabsNavigationProps> = () => {
    const activeTabPath = useLocation().pathname;
    const fileSection: FileSection = useContext(FileContext);
    const theme: Theme = useContext(ThemeContext).theme;
    const invoice: Invoice = useContext(InvoiceContext);
    const activityContext = useContext(ActivityContext);
    const company = useContext(CompanyContext);
    const [activeTabElement, setActiveTabElement] = useState<HTMLElement>();

    const [tabHighlighterAttributes, setTabHighlighterAttributes] = useState<TabHighlighterAttributes>({
        left: 0,
        width: 0
    });

    useEffect(() => {
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        }
    }, [false]);

    const onResize = () => {
        updateTabHighlighterAttributes(document.getElementsByClassName('active-tab')[0] as HTMLElement);
    }


    useEffect(() => {
        updateTabHighlighterAttributes();
    }, [activeTabElement, activityContext.isActivityLoading]);

    useEffect(() => {
        updateTabHighlighterAttributes(document.getElementsByClassName('tab')[0] as HTMLElement);
    }, [invoice]);

    useEffect(() => {
        if (!activeTabElement) {
            setActiveTabElement(document.getElementsByClassName('tab')[0] as HTMLElement);
        }
        updateTabHighlighterAttributes();
    }, [activeTabElement]);

    const updateTabHighlighterAttributes = (activeTabElementFallback: HTMLElement | null = null) => {
        const activeTabElementForUpdate = activeTabElement || activeTabElementFallback;
        if (activeTabElementForUpdate) {
            const {offsetLeft, clientWidth} = activeTabElementForUpdate as HTMLElement;

            if (offsetLeft !== null && clientWidth) {
                setTabHighlighterAttributes({width: clientWidth, left: offsetLeft});
            }
        }
    };

    const getTabHighlighterStyles = (): CSSProperties => {
        return {
            width: `${tabHighlighterAttributes.width}px`,
            left: `${tabHighlighterAttributes.left}px`,
            background: theme.branding_color
        };
    };

    const getTabClassname = (path: string) => {
        return `tab ${isTabActive(path) ? 'active-tab' : ''}`;
    }

    const setActiveTabElementRef = (path: string) => {
        return (element: any) => {
            if (isTabActive(path)) {
                setActiveTabElement(element);
            }
        }
    }

    const isTabActive = (path: string) => {
        return path === activeTabPath;
    }

    const renderTabComponent = (title: string, path: string) => {
        // Preserve search params like authorization "token"
        const pathWithSearchParams = path + window.location.search;

        return <Link to={pathWithSearchParams}>
            <div ref={setActiveTabElementRef(path)} className={getTabClassname(path)}>
                {title}
            </div>
        </Link>
    };

    const isOffline = isOfflineMode();

    return (
        <nav className='tabs'>
            {renderTabComponent('General', '/')}
            {(invoice.notes && invoice.notes.length > 0) && renderTabComponent('Notes', '/notes')}
            {(invoice.is_contract_content_visible && invoice.contract_content && invoice.contract_content.length > 0) && renderTabComponent('Contract', '/contract')}
            {fileSection.is_files_visible && renderTabComponent('Files', '/files')}
            {!isOffline && company.can_customer_comment_public_link && renderTabComponent('Comments', '/comments')}
            {(!activityContext.isActivityLoading && activityContext.activity && activityContext.activity.length !== 0) && renderTabComponent('Activity', '/activity')}

            <div style={getTabHighlighterStyles()} className='tab-highlighter'/>
        </nav>
    );
};
